import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';
import AppService from '../../../Configurations/Appservice';

/**
 * Organization Bank Mapping Component
 *
 * Allows organizations to enable/disable banks for their organization
 * Features:
 * - View available banks
 * - Enable/disable bank access per organization via radio buttons
 */
const OrganizationBankMapping = ({ organizationId }) => {
  const [banks, setBanks] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch available banks for this organization (with mapping IDs)
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      console.log('[OrganizationBankMapping] Loading all banks with status for org:', organizationId);

      // Fetch organization's banks with their enable/disable status
      // This endpoint returns ALL banks (active and inactive) with their current status for this organization
      const mappingsData = await AppService.get(
        `/v1/bank-accounts/admin/organization/${organizationId}/banks-with-status`
      );
      setMappings(mappingsData);
      console.log('[OrganizationBankMapping] Loaded', mappingsData.length, 'banks with status');
      console.log('[OrganizationBankMapping] Banks data:', mappingsData);
      if (mappingsData.length > 0) {
        console.log('[OrganizationBankMapping] First bank object structure:', JSON.stringify(mappingsData[0], null, 2));
        console.log('[OrganizationBankMapping] Mapping ID (use this for PUT):', mappingsData[0].mappingId);
      }

      setError(null);
    } catch (err) {
      console.error('[OrganizationBankMapping] Error loading data:', err);
      setError(err.message || 'Failed to load bank data');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (organizationId) {
      loadData();
    }
  }, [organizationId, loadData]);

  // Handle enable/disable status change for a bank
  const handleToggleStatus = async (bankId, newStatus) => {
    console.log('[OrganizationBankMapping] Updating bank ID:', bankId, 'to', newStatus);

    const bank = mappings.find(b => b.bankId === bankId);
    console.log('[OrganizationBankMapping] Found bank:', bank);

    try {
      setLoading(true);

      if (!bank) {
        console.error('[OrganizationBankMapping] Bank not found for ID:', bankId);
        console.log('[OrganizationBankMapping] Available banks:', mappings);
        setError('Bank not found');
        setLoading(false);
        return;
      }

      // Update the bank status using the new admin endpoint
      // This updates the mapping status for this organization
      const payload = {
        status: newStatus,  // ACTIVE or INACTIVE
      };

      console.log('[OrganizationBankMapping] Updating bank ID:', bankId);
      console.log('[OrganizationBankMapping] Sending PUT payload:', JSON.stringify(payload));

      const response = await AppService.put(
        `/v1/bank-accounts/admin/organization/${organizationId}/bank/${bankId}/status`,
        payload
      );
      console.log('[OrganizationBankMapping] Update response:', response);

      setSuccess(`Bank ${newStatus === 'ACTIVE' ? 'enabled' : 'disabled'} successfully`);
      await loadData();
      setLoading(false);
    } catch (err) {
      console.error('[OrganizationBankMapping] Error toggling status:', err);
      console.error('[OrganizationBankMapping] Error response:', err.response?.data);
      setError(err.response?.data?.message || err.message || 'Failed to update bank status');
      setLoading(false);
    }
  };

  if (loading && mappings.length === 0) {
    return (
      <Box display="flex" justifyContent="center" my={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Enable or disable banks for this organization. Use the radio buttons below to control which banks are available.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Available Banks Section with Enable/Disable Controls */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckIcon sx={{ color: '#064d51' }} />
          Available Banks
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {mappings.length > 0 ? (
          <Grid container spacing={2}>
            {mappings.map(bank => {
              console.log('[OrganizationBankMapping] Rendering bank:', bank.id, 'Status:', bank.status);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={bank.id}>
                  <Card sx={{
                    height: '100%',
                    border: '2px solid #064d51',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    opacity: bank.status === 'INACTIVE' ? 0.7 : 1,
                    transition: 'opacity 0.3s'
                  }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {/* Bank Name Header */}
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#064d51', fontSize: '1rem', mb: 1 }}>
                        {bank.bankName}
                      </Typography>

                      {/* Account Details */}
                      <Box sx={{ fontSize: '0.75rem', mb: 2 }}>
                        {bank.accountNumber && (
                          <>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25, fontSize: '0.65rem' }}>
                              Account #
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: '500', mb: 0.75, display: 'block', fontSize: '0.7rem' }}>
                              {bank.accountNumber}
                            </Typography>
                          </>
                        )}
                        {bank.iban && (
                          <>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25, fontSize: '0.65rem' }}>
                              IBAN
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', mb: 0.75, display: 'block' }}>
                              {bank.iban}
                            </Typography>
                          </>
                        )}
                      </Box>

                      {/* Enable/Disable Radio Buttons */}
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        pt: 1,
                        borderTop: '1px solid #e0e0e0'
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#064d51', fontSize: '0.7rem' }}>
                          Status
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                            <input
                              type="radio"
                              name={`status-${bank.bankId}`}
                              value="ACTIVE"
                              checked={bank.status === 'ACTIVE'}
                              onChange={() => handleToggleStatus(bank.bankId, 'ACTIVE')}
                              disabled={loading}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>Enabled</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                            <input
                              type="radio"
                              name={`status-${bank.bankId}`}
                              value="INACTIVE"
                              checked={bank.status === 'INACTIVE'}
                              onChange={() => handleToggleStatus(bank.bankId, 'INACTIVE')}
                              disabled={loading}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>Disabled</span>
                          </label>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Alert severity="info" sx={{ py: 1 }}>
            No banks available. Please check the system bank configuration.
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default OrganizationBankMapping;
